"use client";

import { useEffect, useId, useRef, useState } from "react";

/**
 * Accessible "what do these numbers mean?" popover explaining gauge as string
 * thickness. No native dialog (browser modals block automation); toggled button
 * + Escape / outside-click to dismiss. Reused on cards, product detail, and the
 * configurator so the gauge explanation is consistent everywhere.
 */
export function GaugeInfo({ className }: { className?: string }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    function onClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  return (
    <span ref={wrapRef} className={`relative inline-flex ${className ?? ""}`}>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label="What do these gauge numbers mean?"
        className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold leading-none transition-colors"
        style={{ backgroundColor: "#2A2A2A", color: "#E8C547" }}
      >
        ?
      </button>
      {open && (
        <span
          id={panelId}
          role="tooltip"
          className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 rounded-md border text-left shadow-xl"
          style={{ backgroundColor: "#1A1A1A", borderColor: "#3A3A3A" }}
        >
          <span className="block text-xs font-bold mb-1" style={{ color: "#F5F5F5" }}>
            Gauge = string thickness
          </span>
          <span className="block text-xs mb-2" style={{ color: "#B0B0B0" }}>
            Each number is one string&apos;s diameter in inches. A 9&ndash;42
            six-string set runs from a thin .009&Prime; high E to a thicker
            .042&Prime; low E &mdash; the count of numbers equals the number of
            strings.
          </span>
          <span className="block text-xs" style={{ color: "#B0B0B0" }}>
            The letter <span style={{ color: "#E8C547" }}>w</span> marks a wound
            string (a wrap wire around the core, common on thicker strings).
            Bigger numbers mean more tension.
          </span>
        </span>
      )}
    </span>
  );
}
